'use client';

import { motion } from 'framer-motion';
import { useState } from 'react';
import { CTA } from '@/components/CTA/CTA';
import { getSectionMotionHeading } from '@/components/dynamic-zone/section-motion-heading';
import { cn } from '@/lib/utils';
import { components } from '@/types/strapi';
import MediaCarousel from '../../media-carousel/MediaCarousel';

// Define a type for Strapi image formats
interface ImageFormat {
	url: string;
	width: number;
	height: number;
}

interface StrapiImageFormats {
	large?: ImageFormat;
	medium?: ImageFormat;
	small?: ImageFormat;
	thumbnail?: ImageFormat;
}

export default function TwoColumnFeatures({
	heading = 'Covering All Your Bulk SMS Messaging Needs',
	subHeading = 'Be It Promotional Or Transactional',
	features = [
		{
			text: 'Promotional Bulk msg can be used to promote their products and services, or special offers to their customers.',
		},
		{
			text: 'Transactional Bulk msg can be used for order confirmations, delivery updates, account alerts and one-time passwords (OTPs).',
		},
	],
	images = [],
	imagePosition = 'right',
	backgroundColor = 'bg-gradient-to-b from-blue-50 to-white',
	CTAs = [],
	isFirst = false,
}: components['schemas']['DynamicZoneTwoColumnFeaturesComponent'] & {
	isFirst?: boolean;
}) {
	const SectionHeading = getSectionMotionHeading(isFirst);
	const [hoveredFeature, setHoveredFeature] = useState<number | null>(null);

	const STRAPI_URL = process.env.NEXT_PUBLIC_STRAPI_URL ?? '';

	// Format images for the MediaCarousel component
	const formattedImages =
		images?.map(img => {
			// Handle different Strapi image formats without type errors
			let url = '';
			const alternativeText = img.alternativeText || '';

			// Check if the image has formats property (Strapi v4 format)
			if ('formats' in img && img.formats) {
				// Cast to the correct type to avoid TS errors
				const formats = img.formats as unknown as StrapiImageFormats;

				// Choose the best available format
				const formatSource =
					formats.large?.url ||
					formats.medium?.url ||
					formats.small?.url ||
					formats.thumbnail?.url;

				if (formatSource) {
					url = formatSource.startsWith('/')
						? `${STRAPI_URL}${formatSource}`
						: formatSource;
				}
			}

			// If no formats, use the url property directly
			if (!url && 'url' in img && img.url) {
				url = img.url.startsWith('/')
					? `${STRAPI_URL}${img.url}`
					: img.url;
			}

			return {
				url,
				alternativeText,
			};
		}) || [];

	// Animation variants
	const containerVariants = {
		hidden: { opacity: 0 },
		visible: {
			opacity: 1,
			transition: {
				staggerChildren: 0.1,
				delayChildren: 0.2,
			},
		},
	};

	const itemVariants = {
		hidden: { y: 20, opacity: 0 },
		visible: {
			y: 0,
			opacity: 1,
			transition: { duration: 0.5 },
		},
	};

	return (
		<section
			className={cn(
				'two-column-features-section px-4 py-12 sm:px-6 sm:py-16 lg:px-8 lg:py-20',
				backgroundColor,
			)}
			data-testid="two-column-features">
			<noscript>
				<style>{`
					.two-column-features-section h2[style],
					.two-column-features-section p[style],
					.two-column-features-section li[style],
					.two-column-features-section [style*='opacity'],
					.two-column-features-section [style*='translateY'],
					.two-column-features-section [style*='translateX'] {
						opacity: 1 !important;
						transform: none !important;
					}
				`}</style>
			</noscript>
			<div className="mx-auto max-w-7xl">
				<motion.div
					className="mx-auto mb-8 max-w-3xl text-center"
					initial={{ opacity: 0, y: -10 }}
					whileInView={{ opacity: 1, y: 0 }}
					viewport={{ once: true }}
					transition={{ duration: 0.5 }}>
					<SectionHeading className="mb-4 text-2xl font-bold leading-tight text-gray-900 sm:text-3xl lg:text-4xl">
						{heading}
					</SectionHeading>
					<p className="text-lg leading-relaxed text-gray-600 sm:text-xl">
						{subHeading}
					</p>
				</motion.div>

				<div
					className={cn(
						'grid grid-cols-1 gap-8 md:grid-cols-2 md:gap-12 lg:gap-16 xl:gap-20',
						'items-center',
					)}>
					<motion.div
						className={cn(
							'flex flex-col justify-center',
							'space-y-8',
							imagePosition === 'right'
								? 'lg:order-1'
								: 'lg:order-2',
						)}
						variants={containerVariants}
						initial="hidden"
						whileInView="visible"
						viewport={{ once: true }}>
						{features.map((feature, index) => (
							<motion.div
								key={index}
								className={cn(
									'flex flex-col gap-2 rounded-lg p-4 transition-all duration-300',
									hoveredFeature === index
										? 'scale-[1.02] transform shadow-md'
										: 'hover:shadow-sm',
								)}
								variants={itemVariants}
								onMouseEnter={() => setHoveredFeature(index)}
								onMouseLeave={() => setHoveredFeature(null)}
								onFocus={() => setHoveredFeature(index)}
								onBlur={() => setHoveredFeature(null)}
								tabIndex={0}
								role="group"
								aria-label={`Feature ${index + 1}`}>
								<div className="flex items-start gap-2.5">
									<div
										className={cn(
											'mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-blue-500 transition-all duration-300',
											hoveredFeature === index
												? 'scale-110'
												: '',
										)}
										aria-hidden="true">
										<svg
											className="h-2.5 w-2.5 text-white"
											fill="none"
											viewBox="0 0 24 24"
											stroke="currentColor">
											<path
												strokeLinecap="round"
												strokeLinejoin="round"
												strokeWidth={2}
												d="M5 13l4 4L19 7"
											/>
										</svg>
									</div>
									<div className="-mt-1">
										{feature.title && (
											<h3 className="text-lg font-medium">
												{feature.title}
											</h3>
										)}
										{!feature.title && (
											<p className="text-base leading-relaxed md:text-lg">
												{feature.text}
											</p>
										)}
										{feature.title && (
											<p className="mt-1 text-base leading-relaxed md:text-lg">
												{feature.text}
											</p>
										)}
									</div>
								</div>
							</motion.div>
						))}
					</motion.div>
					<motion.div
						className={cn(
							'relative h-full w-full',
							'flex items-center justify-center',
							imagePosition === 'right'
								? 'lg:order-2'
								: 'lg:order-1',
						)}
						initial={{
							opacity: 0,
							x: imagePosition === 'right' ? 30 : -30,
						}}
						whileInView={{ opacity: 1, x: 0 }}
						viewport={{ once: true }}
						transition={{ duration: 0.7, delay: 0.2 }}>
						<MediaCarousel images={formattedImages} />
					</motion.div>
				</div>

				{CTAs && CTAs.length > 0 && (
					<motion.div
						className="mt-12 flex items-center justify-center"
						initial={{ opacity: 0, y: 10 }}
						whileInView={{ opacity: 1, y: 0 }}
						viewport={{ once: true }}
						transition={{ duration: 0.5, delay: 0.2 }}>
						<CTA CTAs={CTAs} />
					</motion.div>
				)}
			</div>
		</section>
	);
}
